/**
 * Wave CF — Court Forms: firm setup (practice areas → jurisdictions → forms),
 * case-type packets, and the form library.
 *
 * Staff see the library of forms their firm has enabled. Firm administrators also
 * get Firm setup and Packets. Standing React rule: sub-components at module scope.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, Gavel, OpenInNew, PlaylistAddCheck, RestartAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { isFirmAdmin } from '../../utils/permissions';
import PlatformLibraryTab from './PlatformLibraryTab';

type Ref = { key: string; label: string };
type CaseTypeRef = { key: string; label: string; practiceArea: string; jurisdiction: string };
type LibraryForm = {
  id: string; issuer: string; code: string; title: string; kind: string; jurisdiction: string; practiceArea: string;
  editionDate: string | null; fillable: string; pageCount: number; enabled: boolean; topics?: string[];
};

async function api(url: string, init?: RequestInit, fallback = 'Request failed') {
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${getAuthToken()}`, ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res.json();
}

async function previewForm(id: string) {
  try {
    const res = await fetch(`/api/court-forms/library/${id}/file`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to open form'));
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error(toUserErrorMessage(err, 'Unable to open form'));
  }
}

const edition = (d: string | null) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : '—');

// ---------------------------------------------------------------------------- Firm setup

interface SetupProps {
  reference: any;
  onSaved: () => void;
}

const FirmSetup: React.FC<SetupProps> = ({ reference, onSaved }) => {
  const [areas, setAreas] = useState<string[]>(reference.settings.practiceAreas || []);
  const [juris, setJuris] = useState<string[]>(reference.settings.jurisdictions || []);
  const [autoAttach, setAutoAttach] = useState<boolean>(reference.settings.autoAttach !== false);

  useEffect(() => {
    setAreas(reference.settings.practiceAreas || []);
    setJuris(reference.settings.jurisdictions || []);
    setAutoAttach(reference.settings.autoAttach !== false);
  }, [reference]);

  const coverage = (area: string, j?: string) =>
    (reference.coverage as any[]).filter((c) => c.practiceArea === area && (!j || c.jurisdiction === j)).reduce((s, c) => s + c.forms, 0);
  const coverageByJ = (j: string) => (reference.coverage as any[]).filter((c) => c.jurisdiction === j).reduce((s, c) => s + c.forms, 0);

  const save = useMutation({
    mutationFn: () => api('/api/court-forms/settings', {
      method: 'PUT', body: JSON.stringify({ practiceAreas: areas, jurisdictions: juris, autoAttach }),
    }, 'Unable to save settings'),
    onSuccess: () => { toast.success('Court form settings saved'); onSaved(); },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to save settings')),
  });
  const recommend = useMutation({
    mutationFn: () => api('/api/court-forms/selections/recommended', { method: 'POST', body: JSON.stringify({}) }, 'Unable to select forms'),
    onSuccess: (r) => { toast.success(`Enabled ${r.data.enabled} forms used by ${r.data.caseTypes} case types`); onSaved(); },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to select forms')),
  });

  const toggle = (list: string[], set: (v: string[]) => void, key: string) =>
    set(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  const access = reference.settings.access || { federal: true, allStates: true, states: [], plan: '' };
  const states: Ref[] = reference.jurisdictions.filter(
    (j: Ref) => j.key !== 'US-FED' && (access.allStates || access.states.includes(j.key))
  );
  const stateName = (k: string) => reference.jurisdictions.find((j: Ref) => j.key === k)?.label || k;
  const planNote = access.allStates && access.federal ? ''
    : `Your plan includes ${[
      access.federal ? 'Federal forms' : '',
      access.allStates ? 'every state' : access.states.length ? access.states.map(stateName).join(', ') : '',
    ].filter(Boolean).join(' and ') || 'no court forms'}${!access.allStates && !access.states.length && access.federal ? ' only' : ''}. Contact ArgisTech support to add ${access.plan === 'professional' && !access.states.length ? 'your state' : 'more states'}.`;

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">1. Practice areas</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>The kinds of matters your firm handles.</Typography>
        <FormGroup row>
          {reference.practiceAreas.map((p: Ref) => (
            <FormControlLabel
              key={p.key}
              sx={{ minWidth: 260 }}
              control={<Checkbox checked={areas.includes(p.key)} onChange={() => toggle(areas, setAreas, p.key)} />}
              label={<span>{p.label} <Typography component="span" variant="caption" color="text.secondary">({coverage(p.key)} forms)</Typography></span>}
            />
          ))}
        </FormGroup>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">2. Jurisdictions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Immigration is federal — choosing it always includes USCIS and EOIR forms. States with no forms in the library yet show 0.
        </Typography>
        {planNote && <Alert severity="info" sx={{ mb: 1 }}>{planNote}</Alert>}
        {access.federal && (
          <FormControlLabel
            control={<Checkbox checked={juris.includes('US-FED') || areas.includes('immigration')} disabled={areas.includes('immigration')} onChange={() => toggle(juris, setJuris, 'US-FED')} />}
            label={`Federal (USCIS / EOIR) — ${coverageByJ('US-FED')} forms`}
          />
        )}
        {states.length > 0 && <Autocomplete
          multiple
          sx={{ mt: 1 }}
          options={states}
          value={states.filter((s) => juris.includes(s.key))}
          onChange={(_, v) => setJuris([...juris.filter((j) => j === 'US-FED'), ...v.map((s) => s.key)])}
          getOptionLabel={(s) => `${s.label} (${s.key}) — ${coverageByJ(s.key)} forms`}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          renderInput={(params) => <TextField {...params} label="States" placeholder="e.g. California, Texas, Louisiana, New Mexico" />}
        />}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">3. When a matter is opened</Typography>
        <FormControlLabel
          control={<Switch checked={autoAttach} onChange={(e) => setAutoAttach(e.target.checked)} />}
          label="Automatically add the case type's required forms to the Matter Folder (as drafts for attorney review)"
        />
      </Paper>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </Button>
        <Tooltip title="Switch on every form used by the recommended packets for your practice areas and jurisdictions">
          <span>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddCheck />}
              disabled={!reference.settings.configured || recommend.isPending}
              onClick={() => recommend.mutate()}
            >
              Enable recommended forms
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {!reference.settings.configured && (
        <Alert severity="info">Save your practice areas and jurisdictions first, then enable the recommended forms.</Alert>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------- Library

interface LibraryProps {
  admin: boolean;
  reference: any;
}

const LibraryTab: React.FC<LibraryProps> = ({ admin, reference }) => {
  const queryClient = useQueryClient();
  const [area, setArea] = useState('');
  const [juris, setJuris] = useState('');
  const [search, setSearch] = useState('');
  const [onlyEnabled, setOnlyEnabled] = useState(!admin);
  const [showInstructions, setShowInstructions] = useState(false);

  const params = new URLSearchParams();
  if (area) params.set('practiceArea', area);
  if (juris) params.set('jurisdiction', juris);
  if (search.trim().length >= 2) params.set('search', search.trim());
  if (onlyEnabled) params.set('enabled', 'true');
  if (!showInstructions) params.set('kind', 'form');

  const { data, isLoading } = useQuery({
    queryKey: ['court-forms', 'library', params.toString()],
    queryFn: () => api(`/api/court-forms/library?${params}`, undefined, 'Unable to load forms'),
  });
  const forms: LibraryForm[] = data?.data || [];

  const toggle = useMutation({
    mutationFn: (f: LibraryForm) => api('/api/court-forms/selections', {
      method: 'PUT', body: JSON.stringify(f.enabled ? { disable: [{ issuer: f.issuer, code: f.code }] } : { enable: [{ issuer: f.issuer, code: f.code }] }),
    }, 'Unable to update'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['court-forms'] }),
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to update')),
  });

  const areaLabel = useMemo(() => Object.fromEntries(reference.practiceAreas.map((p: Ref) => [p.key, p.label])), [reference]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Search form number or title" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="cf-area">Practice area</InputLabel>
            <Select labelId="cf-area" label="Practice area" value={area} onChange={(e) => setArea(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {reference.practiceAreas.map((p: Ref) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="cf-j">Jurisdiction</InputLabel>
            <Select labelId="cf-j" label="Jurisdiction" value={juris} onChange={(e) => setJuris(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {reference.settings.access?.federal !== false && <MenuItem value="US-FED">Federal</MenuItem>}
              {Array.from(new Set((reference.coverage as any[]).map((c) => c.jurisdiction))).filter((j) => j !== 'US-FED' && (!reference.settings.access || reference.settings.access.allStates || reference.settings.access.states.includes(j))).map((j) => (
                <MenuItem key={j} value={j}>{j}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          {admin && (
            <FormControlLabel control={<Switch checked={onlyEnabled} onChange={(e) => setOnlyEnabled(e.target.checked)} />} label="Enabled only" />
          )}
          <FormControlLabel control={<Switch checked={showInstructions} onChange={(e) => setShowInstructions(e.target.checked)} />} label="Instructions" />
        </Grid>
      </Grid>

      {isLoading && <CircularProgress size={28} />}
      {!isLoading && !forms.length && (
        <Alert severity="info">
          {onlyEnabled ? 'No forms enabled for your firm match. ' : 'No forms match. '}
          {admin ? 'Use Firm setup to choose practice areas and enable forms.' : 'Ask a firm administrator to enable forms.'}
        </Alert>
      )}
      {forms.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {admin && <TableCell padding="checkbox">On</TableCell>}
                <TableCell>Form</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Edition</TableCell>
                <TableCell>Fillable</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forms.map((f) => (
                <TableRow key={f.id} hover>
                  {admin && (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={f.enabled} disabled={toggle.isPending} onChange={() => toggle.mutate(f)} />
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <strong>{f.code}</strong>{f.kind !== 'form' && <Chip size="small" label={f.kind} sx={{ ml: 1 }} />}
                    <Typography variant="caption" color="text.secondary" display="block">{f.issuer} · {f.jurisdiction}</Typography>
                  </TableCell>
                  <TableCell>{f.title}</TableCell>
                  <TableCell>{areaLabel[f.practiceArea] || f.practiceArea}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{edition(f.editionDate)}</TableCell>
                  <TableCell>{f.fillable === 'acroform' ? 'Yes' : f.fillable === 'xfa_only' ? 'Adobe only' : 'Print'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" aria-label={`Preview ${f.code}`} onClick={() => previewForm(f.id)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------- Packets

const PacketEditor: React.FC<{ caseType: CaseTypeRef }> = ({ caseType }) => {
  const queryClient = useQueryClient();
  const key = ['court-forms', 'packet', caseType.key];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api(`/api/court-forms/packets/${caseType.key}`) });
  const { data: libData } = useQuery({
    queryKey: ['court-forms', 'library', 'enabled-forms'],
    queryFn: () => api('/api/court-forms/library?enabled=true&kind=form'),
    staleTime: 60000,
  });
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => { if (data?.data) setItems(data.data.items); }, [data]);
  const library: LibraryForm[] = libData?.data || [];

  const save = useMutation({
    mutationFn: () => api(`/api/court-forms/packets/${caseType.key}`, {
      method: 'PUT', body: JSON.stringify({ items: items.filter((i) => i.inLibrary !== false).map((i) => ({ issuer: i.issuer, code: i.form?.code || i.code, required: i.required })) }),
    }, 'Unable to save packet'),
    onSuccess: () => { toast.success('Packet saved for your firm'); queryClient.invalidateQueries({ queryKey: key }); },
    onError: (e: Error) => toast.error(toUserErrorMessage(e, 'Unable to save packet')),
  });
  const reset = useMutation({
    mutationFn: () => api(`/api/court-forms/packets/${caseType.key}`, { method: 'DELETE' }, 'Unable to reset'),
    onSuccess: () => { toast.success('Packet reset to the recommended forms'); queryClient.invalidateQueries({ queryKey: key }); },
  });

  if (isLoading) return <CircularProgress size={24} />;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip size="small" label={data?.data?.source === 'firm' ? 'Customized for your firm' : 'Recommended packet'} color={data?.data?.source === 'firm' ? 'primary' : 'default'} />
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Form</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Auto-add</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((i, idx) => (
            <TableRow key={`${i.issuer}:${i.code}`}>
              <TableCell><strong>{i.form?.code || i.code}</strong></TableCell>
              <TableCell>{i.form?.title || '—'}</TableCell>
              <TableCell>
                <Switch
                  size="small"
                  checked={Boolean(i.required)}
                  onChange={(e) => setItems((list) => list.map((x, j) => (j === idx ? { ...x, required: e.target.checked } : x)))}
                />
              </TableCell>
              <TableCell>
                {!i.inLibrary ? <Chip size="small" color="error" variant="outlined" label="Missing from library" />
                  : i.inPlan === false ? <Chip size="small" variant="outlined" label="Not in your plan" />
                  : !i.enabled ? <Chip size="small" color="warning" label="Not enabled" />
                    : <Chip size="small" color="success" variant="outlined" label="Ready" />}
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" aria-label="Remove" onClick={() => setItems((list) => list.filter((_, j) => j !== idx))}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Autocomplete
        sx={{ mt: 2, maxWidth: 520 }}
        size="small"
        options={library.filter((f) => !items.some((i) => i.issuer === f.issuer && (i.form?.code || i.code) === f.code))}
        getOptionLabel={(f) => `${f.code} — ${f.title}`}
        groupBy={(f) => f.issuer}
        value={null}
        onChange={(_, f) => f && setItems((list) => [...list, {
          issuer: f.issuer, code: f.code, required: false, inLibrary: true, enabled: true,
          form: { id: f.id, code: f.code, title: f.title, editionDate: f.editionDate },
        }])}
        renderInput={(params) => <TextField {...params} label="Add an enabled form to this packet" />}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" size="small" onClick={() => save.mutate()} disabled={save.isPending}>Save packet</Button>
        <Button size="small" startIcon={<RestartAlt />} onClick={() => reset.mutate()} disabled={reset.isPending || data?.data?.source !== 'firm'}>
          Reset to recommended
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        &quot;Auto-add&quot; forms are copied into a new matter automatically. Others are offered as optional in the matter&apos;s Court Forms tab.
        Recommended packets are a starting point, not legal advice.
      </Typography>
    </Box>
  );
};

const PacketsTab: React.FC<{ reference: any }> = ({ reference }) => {
  const { settings } = reference;
  const types: CaseTypeRef[] = (reference.caseTypes as CaseTypeRef[]).filter(
    (t) => settings.practiceAreas.includes(t.practiceArea)
      && (t.jurisdiction === 'US-FED' ? settings.access?.federal !== false : settings.jurisdictions.includes(t.jurisdiction))
  );
  const [selected, setSelected] = useState<string>('');
  useEffect(() => { if (!selected && types.length) setSelected(types[0].key); }, [types, selected]);
  const current = types.find((t) => t.key === selected);
  if (!settings.configured || !types.length) {
    return <Alert severity="info">Choose practice areas and jurisdictions in Firm setup first — case types appear here once they match.</Alert>;
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper variant="outlined" sx={{ maxHeight: 640, overflow: 'auto' }}>
          {types.map((t) => (
            <Box
              key={t.key}
              onClick={() => setSelected(t.key)}
              sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: t.key === selected ? 'action.selected' : undefined, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Typography variant="body2">{t.label}</Typography>
              <Typography variant="caption" color="text.secondary">{t.jurisdiction === 'US-FED' ? 'Federal' : t.jurisdiction}</Typography>
            </Box>
          ))}
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          {current && (
            <>
              <Typography variant="h6">{current.label}</Typography>
              <Divider sx={{ my: 1 }} />
              <PacketEditor key={current.key} caseType={current} />
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

// ---------------------------------------------------------------------------- Page

const CourtFormsPage: React.FC = () => {
  const { user } = useAuth() as any;
  const admin = isFirmAdmin(user);
  const platformAdmin = Boolean(user?.isPlatformAdmin);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'library' | 'setup' | 'packets' | 'platform'>(platformAdmin ? 'platform' : admin ? 'setup' : 'library');
  const { data, isLoading, error } = useQuery({
    queryKey: ['court-forms', 'reference'],
    queryFn: () => api('/api/court-forms/reference', undefined, 'Unable to load court forms'),
  });
  const reference = data?.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['court-forms'] });

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Gavel /> Court Forms
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Official USCIS, EOIR and state court forms. Choose the ones your firm uses; they are added to new matters by case type.
      </Typography>
      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{toUserErrorMessage(error, 'Unable to load court forms')}</Alert>}
      {reference && (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            {admin && <Tab value="setup" label="Firm setup" />}
            {admin && <Tab value="packets" label="Case type packets" />}
            <Tab value="library" label="Form library" />
            {platformAdmin && <Tab value="platform" label="Platform library (all firms)" />}
          </Tabs>
          {tab === 'platform' && platformAdmin && <PlatformLibraryTab />}
          {tab === 'setup' && admin && <FirmSetup reference={reference} onSaved={refresh} />}
          {tab === 'packets' && admin && <PacketsTab reference={reference} />}
          {tab === 'library' && <LibraryTab admin={admin} reference={reference} />}
        </>
      )}
    </Box>
  );
};

export default CourtFormsPage;
